import { cva } from 'class-variance-authority';
import { focusRing } from 'components/styles/focusRing';
import { PHONE_INPUT_SIZE } from 'components/styles/phoneInputSize';

/**
 * Chat input class recipes.
 *
 * Ported from Figma `Chat Input` 1447:132028, on the `Chat Input [In review]` page. The set this
 * file used to cite throughout — 1137:27674 — is still in the document and is now called
 * `.Chat [superseded 2026-09-17]`, so every `1137:*` number in this component's history points at a
 * retired drawing. Any node reference below is against the new set unless it says otherwise.
 *
 * Like `TextInput.recipe`, these contain ZERO `dark:` variants — colour comes entirely from the
 * `--ds-chat-input-*` slots that `.ds-chat-input` sets in `components/styles/design-system.css`.
 * One family, written by that one class, per the invariant stated beside `.ds-input` there: this
 * component reads no other component's slots, so nothing tuned for a text input can move it. The
 * mic is the one exception and a deliberate one — see `Actions` below.
 *
 * Every state selector reads Base UI's `Field.Root` attributes through the `group/ti` marker
 * (`data-focused`, `data-invalid`, `data-disabled`), so there is no React state behind the colour
 * changes and the states are reachable from a story or a test by rendering rather than simulating.
 * The one exception is the SHAPE, which cannot be observed from the DOM — see `data-multiline` below.
 *
 * The marker is `/ti` — the SAME name `TextInput` uses — and that is deliberate rather than copied.
 * This component reuses `textInputHelperRecipe` for its helper text, and that recipe turns the text
 * red with `group-data-[invalid]/ti:`. A private `/ci` marker compiles, renders and reads correctly
 * in every other state, and silently leaves the error description in the resting colour.
 *
 * So the GROUP NAME is shared contract even though the slot family is not. The two are independent:
 * slots are written per component to stop one component's colours moving another's, while the group
 * name is how a shared recipe finds the field root it is rendered inside. The same bug arrived a
 * second time from the other direction when the families were split and this component's helper was
 * still asking for the pill's shape — see the `chat` variant in `TextInput.recipe`.
 *
 * The state selectors are written to be MUTUALLY EXCLUSIVE rather than relying on utility sort
 * order, the same discipline the button and text-input recipes use. Precedence, highest first:
 *
 *   disabled → invalid → focused → hover → resting
 *
 * ── The state matrix, and which browser state each Figma column is ────────────────────────────
 *
 * The reworked set draws six columns, crossed with `isMultiline` and `isFilled`:
 *
 *   Default    background/control/default   border/control/default    1px
 *   Hover      background/control/hover     border/control/hover      1px
 *   Active     background/control/default   border/accent/default     2px  + the caret
 *   Focus      background/control/default   border/control/default    1px  + the `Focused` effect
 *   Error      background/control/default   border/destructive        1px
 *   Disabled   background/control/disabled  border/control/disabled   1px
 *
 * That is the same six `TextInput` draws, and the reading is the one that file sets out at length,
 * adopted here verbatim rather than re-derived:
 *
 *   **`Active` is "this field holds the caret."** Any focus produces it — pointer or keyboard. It
 *   is the column that draws the caret, and it is `data-focused`.
 *
 *   **`Focus` is the ACCESSIBILITY ring, and nothing else** — the treatment a control gets when the
 *   user arrives by KEYBOARD. Additive rather than alternative: it does not replace Active's border,
 *   it would add the halo outside it. Which is why its cell looks impossible on its own (a resting
 *   border, no caret, a focused field): it is a layer shown by itself.
 *
 * Read that way `Focus` is not drawn on the box at all, because the keyboard-only half is not
 * expressible on a text control — a `<textarea>` matches `:focus-visible` on a plain mouse click, by
 * UA design, so a halo keyed off it would appear on every click as a second edge around the field.
 * `.ds-input` made the same call. The SEND BUTTON does draw the halo, because it is a `<button>`
 * and `:focus-visible` there genuinely answers a Tab.
 *
 * Two cells the file draws inconsistently, neither reproduced:
 *
 *   - **The `Focused` effect is left on in the two `Active`/`isFilled=No` cells** and absent from
 *     the two filled ones. Consistent with the halo being a separate layer somebody left switched
 *     on, and with the set's own description, which says `Active` is "a 2px border/accent/default
 *     plus a caret" and nothing else.
 *   - **`Active`/`isMultiline=Yes` measures 100px where every sibling measures 98**, because the
 *     stroke grew to 2px inside a hugging frame. The set's description names this itself: the
 *     spacing scale stops at x16, so the multiline height cannot be pinned by a token. It is an
 *     artefact of the drawing, not a height anybody chose.
 *
 * ── Why the accent edge is ONE stroke and not two ─────────────────────────────────────────────
 *
 * `border-width` NEVER changes here, in any state. Figma's `Active` cell is a single 2px stroke
 * where every other cell is 1px, and the obvious build — grow the border — is wrong twice over: a
 * border is layout, so widening it moves the padding box and the value text jumps a pixel on every
 * focus and every tab; and it is what makes the multiline `Active` cell 2px taller than its
 * siblings, which is the artefact above.
 *
 * So the border stays 1px and the second pixel is painted as an inset `box-shadow`, which occupies
 * no layout space:
 *
 *   inset 0 0 0 1px  --ds-chat-input-stroke-extra   the pixel that makes the focused stroke 2px
 *
 * **It resolves to the SAME colour as the border in front of it, which is what makes the result one
 * 2px stroke rather than two 1px ones.** An inset shadow paints from the padding-box edge inward and
 * the border paints just outside that edge, so two identical colours meet flush and read as a single
 * thicker edge — matching the drawing exactly. Give the shadow a different value and you get the
 * double ring this component must not have: a border with a distinct line inside it. The slot
 * defaults to `transparent` so the declaration can be unconditional and only the value switches.
 *
 * ── Where the padding lives ────────────────────────────────────────────────────────────────────
 *
 * On the BOX here, not the control — the opposite of `TextInput`, and deliberately so.
 *
 * `TextInput` pays its padding from inside the input so the element fills the box and every pixel
 * is a click target. That trick needs the control to span the whole box, which it cannot do here:
 * the action buttons occupy the right-hand side of the pill and the bottom of the multi-line box, so
 * the control is a flex sibling rather than the full surface. Paying the inset from the box instead
 * keeps Figma's own numbers — 20 leading, 10 trailing and vertical — legible against the file.
 *
 * They are spelled as arbitrary px values rather than named steps, because a named step is a
 * multiple of 4px — the step whose number is 20 is 80px, not 20px — and the scale has none at
 * 20px. The numbers above are given in prose rather than in utility form for the same reason:
 * `noOffScaleSpacing` matches file TEXT, so a comment naming an off-scale step is itself an
 * offender.
 *
 * The hit area is then recovered explicitly rather than geometrically: `ChatInput` focuses the
 * control on a mousedown anywhere in the box that is not a button. That is strictly better than
 * the padding trick — it also covers the gap between the control and the buttons, which no amount
 * of padding on the control could reach.
 */

/**
 * Marker + layout for `Field.Root`.
 *
 * The `.ds-chat-input` slot class lives HERE rather than on the bordered box, for the reason
 * `textInputRootRecipe` gives: custom properties cascade, so the helper text — which is a SIBLING of
 * the box — would otherwise fall outside their scope and silently resolve to inherited colour.
 */
export const chatInputRootRecipe = cva(['group/ti ds-chat-input', 'relative flex w-full flex-col gap-[4px]']);

/**
 * The bordered box.
 *
 * ── Why the border is always there ─────────────────────────────────────────────────────────────
 *
 * EVERY cell has a stroke — `border/control/default` when resting, `-hover` on hover,
 * `border/accent/default` when focused, `border/destructive` in error, `border/control/disabled`
 * when disabled. The fill is what matches this control to the panel it sits on, and the border is
 * what identifies it as a control; on the broadcast panel the fill role and the panel's own surface
 * role resolve to the SAME value in dark, so the border is the only thing separating the composer
 * from the surface behind it. `chatInputContrast` pins that coincidence as an equality rather than
 * restating the value here, which is also why no hex appears in this file.
 *
 * ── Hover moves the fill now ───────────────────────────────────────────────────────────────────
 *
 * New in the reworked set, which draws a Hover column the previous one did not have. It cannot
 * conflict with focus: the rule is fenced against `data-focused`, and a tap on this box focuses the
 * control, so the sticky `:hover` a touch device leaves behind is excluded before it can paint.
 * (The send button has no such fence — see the note there.)
 */
export const chatInputBoxRecipe = cva(
  [
    'relative flex w-full gap-[4px]',
    'pl-[20px] pr-[10px] py-[10px]',
    'border-solid border-[length:var(--ds-chat-input-border-width)] border-[var(--ds-chat-input-border)]',
    'bg-[var(--ds-chat-input-bg)]',
    'cursor-text',
    'transition-[border-color,background-color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

    // Hover is excluded from every other state so it can never fight them.
    'group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:border-[var(--ds-chat-input-border-hover)]',
    'group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:bg-[var(--ds-chat-input-bg-hover)]',

    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:border-[var(--ds-chat-input-border-caret)]',
    'group-[[data-invalid]:not([data-disabled])]/ti:border-[var(--ds-chat-input-border-invalid)]',
    'group-data-[disabled]/ti:border-[var(--ds-chat-input-border-disabled)]',
    'group-data-[disabled]/ti:bg-[var(--ds-chat-input-bg-disabled)]',
    'group-data-[disabled]/ti:cursor-not-allowed',

    // Off by default, so the declaration below can be unconditional.
    '[--ds-chat-input-stroke-extra:transparent]',
    'shadow-[inset_0_0_0_1px_var(--ds-chat-input-stroke-extra)]',

    /**
     * The second pixel of the focused stroke, in the SAME colour as the border showing in front of
     * it — see the long note in the header. Two identical colours meeting flush is what makes this
     * one 2px accent edge instead of a border with a second line inside it.
     *
     * Excluded from invalid, which keeps the red border at 1px as the design draws it.
     */
    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:[--ds-chat-input-stroke-extra:var(--ds-chat-input-border-caret)]',
  ],
  {
    variants: {
      /**
       * Figma's `isMultiline` axis.
       *
       * `rounded-full` on the pill rather than the 56px radius the file authors: unlike
       * `TextInputPill`, this box GROWS, so a fixed radius equal to the resting height would stop
       * agreeing with the shape the moment a second line appeared. The pill is only ever drawn at
       * its resting height, where the two are identical. The multi-line box is `radius/lg`, 24px.
       *
       * The pill's floor is 56px: 1px of border, 10px of padding, a 32px content row, then the same
       * back again is 54, and the set pins `minHeight: 56` on the Field to reach 56. The multi-line
       * box needs no floor of its own — 1 + 10 + (40 + 4 + 32) + 10 + 1 is 98, which is what every
       * multi-line cell measures, so the control's own two-line minimum produces it.
       *
       * `items-end` in the multi-line shape is what pushes the actions to the right edge under the
       * text — Figma's Container is `counterAxisAlignItems: MAX`. `items-center` in the pill is what
       * centres the 32px action row in the content row.
       */
      multiline: {
        false: 'min-h-[56px] flex-row items-center rounded-full',
        true: 'flex-col items-end rounded-[24px]',
      },
    },
    defaultVariants: { multiline: false },
  }
);

/**
 * The control — a `<textarea>`, which is the whole reason this component exists rather than a
 * variant of `TextInput`: DS `Field.Control` renders an `<input>`, and an `<input>` neither holds a
 * newline nor WRAPS. The wrapping is the part that survives `allowNewlines={false}`, so a consumer
 * that forbids newline entry still needs this element rather than an input.
 *
 * Preflight is deliberately absent from the token layer, so the browser's own textarea styling is
 * still in play and every reset is spelled out. This list is `TextInput`'s `controlBase` minus the
 * `<input>`-only bits (`::-ms-reveal`, `::-webkit-search-cancel-button` — a textarea has neither)
 * and plus the two a textarea needs: `resize-none`, because the design has no drag handle and one
 * would let the user break the pill; and `overflow-y-auto`, which is what `max-h` scrolls against.
 */
export const chatInputControlRecipe = cva(
  [
    'm-0 block w-full appearance-none resize-none border-0 bg-transparent',
    // `text-body-md` rather than an arbitrary size plus a `leading-` utility, which is what this
    // was authored with. The rendered output is identical — the token is 14px/20px/400 — but the
    // scale utility is the only spelling `noHardcodedTypography` accepts, so one Figma text style
    // has one representation in code. Do not restate the old form even in a comment: that guard
    // matches file TEXT, so a comment naming a size is itself an offender.
    //
    // The utility's 20px line height is load-bearing here — every height below counts 20px lines.
    'font-sans text-body-md text-[var(--ds-chat-input-value)]',
    PHONE_INPUT_SIZE,
    'outline-none',
    'placeholder:text-[var(--ds-chat-input-placeholder)]',
    'disabled:cursor-not-allowed disabled:text-[var(--ds-chat-input-value-disabled)]',
    /**
     * The disabled PLACEHOLDER, which needs its own rule and does not get one for free.
     *
     * `disabled:text-*` colours the element, and `::placeholder` is a pseudo-element with its own
     * cascade — so the resting `placeholder:text-*` above kept winning and a disabled empty composer
     * drew its placeholder at `foreground/secondary` in both themes, where the set asks for
     * `foreground/disabled`.
     *
     * The light half is the one that gave it away: there, `foreground/secondary` is DARKER than the
     * `foreground/disabled` a disabled VALUE draws, so a disabled empty field looked more emphatic
     * than a disabled filled one — the opposite of a disabled state, and visible without measuring
     * anything. The measured values are in `chatInputContrast` and the gap log rather than here, so
     * this comment cannot go stale against the ramp.
     */
    'disabled:placeholder:text-[var(--ds-chat-input-value-disabled)]',
    'cursor-text',

    /**
     * The caret, which is Figma's `Active` column drawing a 1px line in `border/accent/default`
     * beside the value. A real textarea has a caret of its own, so this is a COLOUR rather than an
     * element — the design's line is what a caret looks like in a drawing that has no cursor.
     *
     * Not fenced by state: a caret only renders while the element is focused, which is exactly when
     * `Active` applies, so the browser already answers the question a selector would have asked.
     *
     * It reads `-border-caret` rather than a second caret slot of its own. The caret and the focused
     * edge are one accent decision in the drawing, so giving them two slots invited them to drift
     * apart while adding a role nobody else in the family has. One fewer slot, same rendered colour.
     */
    'caret-[var(--ds-chat-input-border-caret)]',

    /**
     * No padding of its own, and the height comes from the line box.
     *
     * The set's `Text` frame is a bare 20px line — one line of `text-body-md` — centred in a 32px
     * content row by the row, not by padding inside the control. An earlier revision paid 8px top
     * and bottom here and ran a 36px minimum, which is what the superseded set drew; reproducing
     * that against the new one would make every shape 4px too tall and put the value off-centre
     * against the 32px buttons beside it.
     *
     * The cap is four 20px lines. The set draws no cap — its tallest cell is two lines — so this is
     * a code decision carried forward from the previous port rather than a number read off the file,
     * and it is now a flat 80px because the control pays no padding to add to it.
     */
    'min-h-[20px] max-h-[80px] p-0',
    'overflow-y-auto',

    /**
     * The 6px pill scrollbar the design draws once the text overflows.
     *
     * Both syntaxes are needed and neither is redundant: `scrollbar-width`/`scrollbar-color` is the
     * standard property that Firefox implements, and the `::-webkit-scrollbar` pseudo-elements are
     * what Chrome and Safari read. A browser that supports the standard property ignores the
     * pseudo-elements, so they cannot disagree.
     */
    '[scrollbar-width:thin] [scrollbar-color:var(--ds-chat-input-scrollbar)_transparent]',
    '[&::-webkit-scrollbar]:w-[6px]',
    '[&::-webkit-scrollbar-track]:bg-transparent',
    '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--ds-chat-input-scrollbar)]',
  ],
  {
    variants: {
      /**
       * `flex-1` with `min-w-0` in the pill so a long unbroken word shrinks the control rather than
       * pushing the actions out of the box; full width in the multi-line shape, where they are on
       * their own row and there is nothing to share with.
       *
       * The multi-line shape also carries a TWO-LINE floor, which is the set's own rule — "min two
       * lines of input", stated in its description and visible in the empty multi-line cell, which
       * reserves 40px of text area with nothing in it. Without the floor an empty multi-line box
       * would be 78px where every drawn cell is 98px.
       *
       * ⚠️ Figma CENTRES a single line inside that 40px and this does not — the placeholder sits at
       * the top, 10px above where the file draws it. The set's `Text` frame is 40 tall with
       * `primaryAxisAlignItems: CENTER`, so it is drawn deliberately rather than by accident, and it
       * is invisible in every other cell: the filled multi-line cell has two lines that fill the
       * frame, so the centring is a no-op there.
       *
       * **It is not expressible on a `<textarea>`.** A textarea lays its text out in an internal
       * block container that `align-items` cannot reach, and `align-content: center` — the one
       * property that would — was measured in Chromium and moved nothing (the line stayed at the
       * top of the 40px box). Padding cannot stand in either: `py` that centres one line adds 20px
       * to the two-line case and takes the box past 98px. Centring it needs a wrapper whose height
       * is measured in JS, which is a lot of machinery for a 10px difference in one cell.
       *
       * Left top-aligned, which is also what every other composer in the category does, and raised
       * with design rather than approximated.
       */
      multiline: {
        false: 'min-w-0 flex-1',
        true: 'w-full min-h-[40px]',
      },
    },
    defaultVariants: { multiline: false },
  }
);

/**
 * The `Actions` row — the mic and the send button, gap 8.
 *
 * A wrapper rather than two flex siblings of the control, because the set has one: in the pill it is
 * a 32px-tall group at the right of the content row, and in the multi-line box it is a row of its
 * own beneath the text. Without it the 8px between the two buttons would have to come from the box's
 * own 4px gap, which also separates the control from them.
 *
 * `shrink-0` matters in the pill: without it a long value squeezes the buttons into ellipses.
 */
export const chatInputActionsRecipe = cva(['flex shrink-0 items-center gap-[8px]']);

/**
 * The send button — 48×32, from `.Send Button` 1447:131994, which is its own component set.
 *
 * Deliberately NOT built on the DS `Button`, and that set's own description gives the reason rather
 * than this file inferring one: "It exists because Button has no size wider than it is tall: the
 * previous composer stretched a 32×32 iconOnly Small Button to 48px, which is an off-library
 * resize." Its tokens are copied from `Button / Size=Small / Type=Primary`, which is why the slots
 * it reads resolve to the accent family's own steps.
 *
 * It was 48×36 with a 20px glyph before this port. The set now draws 48×32 with a 16px glyph, which
 * is what makes it line up with the 32px mic beside it.
 *
 * ── The pressed cell ──────────────────────────────────────────────────────────────────────────
 *
 * `State=Active` on that set, `background/accent/active`. New — the previous composer had no press
 * treatment, so a tap gave no acknowledgement between the press and the message leaving. Written as
 * `[&:not(:disabled):active]` to match `buttonRecipe`'s form, the one difference being that this is
 * a native `<button disabled>` where Base UI's button carries `data-disabled`.
 *
 * ⚠️ A touch tap leaves `:hover` on this button until the next tap elsewhere, so the hover blue
 * sticks after a send. The box above is immune because its hover rule is fenced against
 * `data-focused` and a tap focuses the control; a button has nothing to fence against. The fix is
 * `@media (hover: hover)`, and NOT applied here on purpose: no recipe in this design system uses it
 * yet, `buttonRecipe` has the identical exposure on every button in the product, and fixing one
 * button in one component would make this the only control in the DS that answers a tap differently
 * from its siblings. Recorded as a family-wide gap instead.
 *
 * ── The focus halo, which this element DOES draw ──────────────────────────────────────────────
 *
 * `State=Focus` on that set carries the `Focused` effect style, and unlike the box this element can
 * honour it: `:focus-visible` on a `<button>` answers a Tab and not a click, which is the distinction
 * the box's `<textarea>` cannot make. Painted with the same two-ring shadow `buttonRecipe` uses, off
 * the shared `--effect-focus-*` pair, rather than the `outline` this recipe carried before — an
 * outline is a different shape from the drawn rings, and naming the effect tokens is what keeps the
 * composer's send button and every other DS button ringed identically.
 */
export const chatInputSendRecipe = cva(
  [
    'inline-flex shrink-0 items-center justify-center',
    'h-[32px] w-[48px] rounded-full',
    'm-0 cursor-pointer appearance-none border-0 p-0',
    'bg-[var(--ds-chat-input-send-bg)] text-[var(--ds-chat-input-send-fg)]',
    'scale-100',
    'transition-[background-color,color,scale]',
    'duration-[var(--motion-control)] motion-reduce:transition-none',
    '[&:not(:disabled):active]:duration-[var(--motion-feedback)]',
    'ease-[var(--motion-control-ease)] [&:not(:disabled):active]:ease-[var(--motion-feedback-ease)]',
    'motion-safe:[&:not(:disabled):active]:scale-[0.97]',
    '[&:not(:disabled):hover]:bg-[var(--ds-chat-input-send-bg-hover)]',
    '[&:not(:disabled):active]:bg-[var(--ds-chat-input-send-bg-pressed)]',
    'disabled:cursor-not-allowed',
    'disabled:bg-[var(--ds-chat-input-send-bg-disabled)] disabled:text-[var(--ds-chat-input-send-fg-disabled)]',
    focusRing(),

    /**
     * The hit area, brought to 44px without changing the drawn 48×32 box.
     *
     * WCAG 2.5.8 asks 24×24 and this product asks 44px; the drawn button is 12px short vertically. A
     * pseudo-element is the only way to buy that back here — padding or a min-height would grow the
     * box, and the box is what the pill's 56px height is built out of.
     *
     * The vertical inset is 6px, which lands inside the box's own 10px of padding and cannot reach the
     * border. The horizontal inset is 4px, which is exactly half the 8px gap to the mic, so the two
     * hit areas meet and never overlap — an overlap would give the gap between them to whichever
     * element paints last, and a mis-tap on a send button is a sent message.
     */
    'relative',
    'before:absolute before:-inset-y-[6px] before:-inset-x-[4px] before:content-[""]',

    // Suppresses the double-tap-to-zoom delay on a control that is tapped repeatedly.
    '[touch-action:manipulation]',
  ],
  {
    variants: {
      /**
       * The message is on its way. Figma keeps the RESTING accent fill (a dimmed button reads as "off",
       * this one is working), so every state colour is pinned back to the resting one: hover and press
       * would otherwise answer a control that has just been pressed, and `sendDisabled` may well be
       * true as well, since the caller clears the field on send. The button is `aria-disabled` rather
       * than `disabled`, so a keyboard user who pressed it keeps their focus.
       */
      sending: {
        true: [
          'cursor-progress',
          'data-[sending]:[&:not(:disabled):hover]:bg-[var(--ds-chat-input-send-bg)]',
          'data-[sending]:[&:not(:disabled):active]:bg-[var(--ds-chat-input-send-bg)]',
          'motion-safe:data-[sending]:[&:not(:disabled):active]:scale-100',
          'data-[sending]:disabled:bg-[var(--ds-chat-input-send-bg)] data-[sending]:disabled:text-[var(--ds-chat-input-send-fg)]',
        ],
        false: '',
      },
    },
    defaultVariants: { sending: false },
  }
);

/**
 * The ring that replaces the arrow while a message is sending. It covers the arrow with the button's
 * own fill, and arrives on the shared `--motion-delay`, so a send that resolves first never flashes
 * it; until then the arrow stays, and the button is already inert. HIDDEN rather than absent for the
 * reason `spinnerRecipe` gives: nothing moves when it appears. Unmounted the moment the send ends,
 * because a spinning glyph must not outlive the work.
 */
export const chatInputSendRingRecipe = cva([
  'absolute inset-0 flex items-center justify-center rounded-full',
  'bg-[var(--ds-chat-input-send-bg)] text-[var(--ds-chat-input-send-fg)]',
  'transition-[opacity,visibility] duration-[var(--motion-control)] ease-[var(--motion-control-ease)]',
  'starting:invisible starting:opacity-0 delay-[var(--motion-delay)]',
]);
